/**
 * Simple WebSocket Service for Multiplayer Tic-Tac-Toe
 */

interface GameState {
  roomCode: string;
  board: ('X' | 'O' | '')[][];
  currentPlayer: 'X' | 'O';
  gameStatus: 'waiting' | 'playing' | 'won' | 'draw';
  winner?: 'X' | 'O';
  winningLine?: Array<{ row: number; col: number }>;
  winType?: 'row' | 'column' | 'diagonal-main' | 'diagonal-anti';
  playerCount: number;
  players: Array<{
    playerId: string;
    playerSymbol: 'X' | 'O';
  }>;
}

interface WebSocketMessage {
  action: string;
  roomCode?: string;
  gameState?: GameState;
  move?: {
    row: number;
    col: number;
    player: 'X' | 'O';
  };
  error?: string;
}

export class WebSocketService {
  private static instance: WebSocketService | null = null;
  private ws: WebSocket | null = null;
  private playerSymbol: 'X' | 'O' | null = null;
  private roomCode: string | null = null;
  private onGameStateUpdate: ((state: GameState) => void) | null = null;
  private onError: ((error: string) => void) | null = null;
  
  // Pending operations
  private pendingCreateRoom: ((roomCode: string) => void) | null = null;
  private pendingCreateRoomReject: ((error: Error) => void) | null = null;
  private pendingJoinRoom: (() => void) | null = null;
  private pendingJoinRoomReject: ((error: Error) => void) | null = null;

  // Custom message handler for chess or other games
  private customMessageHandler: ((message: any) => void) | null = null;

  static getInstance(): WebSocketService {
    if (!WebSocketService.instance) {
      WebSocketService.instance = new WebSocketService();
    }
    return WebSocketService.instance;
  }

  /**
   * Check if WebSocket is available/configured
   */
  static isAvailable(): boolean {
    const wsUrl = process.env.REACT_APP_WEBSOCKET_URL;
    return !!(wsUrl && !wsUrl.includes('placeholder') && !wsUrl.includes('example.com'));
  }

  async connect(): Promise<void> {
    // If already connected, return immediately
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      console.log('✅ WebSocket already connected');
      return Promise.resolve();
    }

    // If currently connecting, wait for that connection
    if (this.ws && this.ws.readyState === WebSocket.CONNECTING) {
      console.log('⏳ WebSocket connection already in progress, waiting...');
      return new Promise((resolve, reject) => {
        const checkInterval = setInterval(() => {
          if (this.ws) {
            if (this.ws.readyState === WebSocket.OPEN) {
              clearInterval(checkInterval);
              resolve();
            } else if (this.ws.readyState === WebSocket.CLOSED) {
              clearInterval(checkInterval);
              reject(new Error('WebSocket connection failed'));
            }
          }
        }, 100);
        
        setTimeout(() => {
          clearInterval(checkInterval);
          reject(new Error('WebSocket connection timeout'));
        }, 5000);
      });
    }

    return new Promise((resolve, reject) => {
      // Use AWS WebSocket URL if available, otherwise fallback to localhost
      const wsUrl = process.env.REACT_APP_WEBSOCKET_URL || 'ws://localhost:8080';
      
      // Check if WebSocket is disabled or placeholder
      if (!wsUrl || wsUrl.includes('placeholder') || wsUrl.includes('example.com')) {
        console.warn('⚠️ WebSocket URL not configured. Multiplayer features disabled.');
        reject(new Error('🎮 Multiplayer mode is coming soon! We\'re working on deploying the WebSocket server. Please check back later or try single-player mode.'));
        return;
      }
      
      // Check if URL is valid
      if (!wsUrl.startsWith('ws://') && !wsUrl.startsWith('wss://')) {
        console.error('❌ Invalid WebSocket URL:', wsUrl);
        reject(new Error('Invalid WebSocket configuration. Please contact support.'));
        return;
      }
      
      console.log('🔌 Connecting to WebSocket server at', wsUrl, '...');
      
      try {
        this.ws = new WebSocket(wsUrl);
        console.log('🔌 WebSocket object created, waiting for connection...');

        this.ws.onopen = () => {
          console.log('✅ WebSocket connected successfully');
          resolve();
        };

        this.ws.onmessage = (event) => {
          try {
            // Validate message size (prevent memory exhaustion)
            if (typeof event.data === 'string' && event.data.length > 65536) {
              console.error('❌ Message too large, ignoring:', event.data.length);
              return;
            }
            const message = JSON.parse(event.data);
            // Validate message structure
            if (!message || typeof message !== 'object' || !message.action || typeof message.action !== 'string') {
              console.error('❌ Invalid message structure:', message);
              return;
            }
            this.handleMessage(message);
          } catch (error) {
            console.error('❌ Failed to parse message:', error);
          }
        };

        this.ws.onclose = (event) => {
          console.log('🔌 WebSocket disconnected. Code:', event.code, 'Reason:', event.reason);
          console.log('🔌 Close event details:', {
            code: event.code,
            reason: event.reason,
            wasClean: event.wasClean,
            timestamp: new Date().toISOString()
          });
          console.trace('🔌 WebSocket close stack trace');
        };

        this.ws.onerror = (error) => {
          console.error('❌ WebSocket connection error:', error);
          console.error('❌ Error type:', error.type);
          reject(new Error(`WebSocket connection failed: ${error.type || 'Unknown error'}`));
        };

        // Timeout after 5 seconds
        setTimeout(() => {
          if (this.ws && this.ws.readyState !== WebSocket.OPEN) {
            console.error('❌ Connection timeout - WebSocket state:', this.ws.readyState);
            reject(new Error('WebSocket connection timeout'));
          }
        }, 5000);
        
      } catch (error) {
        console.error('❌ Failed to create WebSocket object:', error);
        reject(error);
      }
    });
  }

  async createRoom(gameType: string = 'tictactoe'): Promise<string> {
    console.log('🎮 Starting room creation process...');
    
    // Ensure connection
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      console.log('🔌 WebSocket not connected, establishing connection...');
      try {
        await this.connect();
        console.log('✅ Connection established for room creation');
      } catch (error) {
        console.error('❌ Failed to connect for room creation:', error);
        throw error;
      }
    } else {
      console.log('✅ WebSocket already connected');
    }

    return new Promise((resolve, reject) => {
      const roomCode = this.generateRoomCode();
      console.log('🎮 Generated room code:', roomCode);
      
      this.roomCode = roomCode;
      this.playerSymbol = 'X';

      // Set up pending operation handlers
      this.pendingCreateRoom = (code) => {
        console.log('✅ Room creation promise resolved with code:', code);
        resolve(code);
      };
      this.pendingCreateRoomReject = (error) => {
        console.error('❌ Room creation promise rejected:', error.message);
        reject(error);
      };

      // Send create room message with gameType:action format
      const message = {
        action: `${gameType}:createRoom`,
        roomCode,
        playerId: `player-${Date.now().toString(36)}`
      };
      
      console.log('📤 Sending createRoom message:', JSON.stringify(message));
      
      try {
        this.sendMessage(message as any);
        console.log('✅ Create room message sent successfully');
      } catch (error) {
        console.error('❌ Failed to send create room message:', error);
        this.pendingCreateRoom = null;
        this.pendingCreateRoomReject = null;
        reject(error);
        return;
      }

      // Timeout after 10 seconds
      setTimeout(() => {
        if (this.pendingCreateRoom) {
          console.error('⏰ Room creation timeout after 10 seconds');
          this.pendingCreateRoom = null;
          this.pendingCreateRoomReject = null;
          reject(new Error('Room creation timeout - server did not respond'));
        }
      }, 10000);
    });
  }

  async joinRoom(roomCode: string, gameType: string = 'tictactoe'): Promise<void> {
    console.log('🎮 Joining room:', roomCode);
    
    // Ensure connection
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      await this.connect();
    }

    return new Promise((resolve, reject) => {
      this.roomCode = roomCode;
      this.playerSymbol = 'O';

      // Set up pending operation handlers
      this.pendingJoinRoom = resolve;
      this.pendingJoinRoomReject = reject;

      // Send join room message with gameType:action format
      this.sendMessage({
        action: `${gameType}:joinRoom`,
        roomCode,
        playerId: `player-${Date.now().toString(36)}`
      } as any);

      console.log('📤 Sent joinRoom message for:', roomCode);

      // Timeout after 10 seconds
      setTimeout(() => {
        if (this.pendingJoinRoom) {
          this.pendingJoinRoom = null;
          this.pendingJoinRoomReject = null;
          reject(new Error('Room join timeout'));
        }
      }, 10000);
    });
  }

  makeMove(row: number, col: number, gameType: string = 'tictactoe'): void {
    if (!this.ws || !this.roomCode || !this.playerSymbol) return;

    console.log('🎯 Making move:', this.playerSymbol, 'at', row, col);
    this.sendMessage({
      action: `${gameType}:move`,
      roomCode: this.roomCode,
      move: {
        row,
        col,
        player: this.playerSymbol
      }
    } as any);
  }

  playAgain(): void {
    if (!this.ws || !this.roomCode) return;

    console.log('🔄 Requesting play again in room:', this.roomCode);
    this.sendMessage({
      action: 'play_again',
      roomCode: this.roomCode
    });
  }

  onGameUpdate(callback: (state: GameState) => void): void {
    this.onGameStateUpdate = callback;
  }

  onErrorOccurred(callback: (error: string) => void): void {
    this.onError = callback;
  }

  getPlayerSymbol(): 'X' | 'O' | null {
    return this.playerSymbol;
  }

  getRoomCode(): string | null {
    return this.roomCode;
  }

  disconnect(): void {
    console.log('🔌 WebSocketService.disconnect() called', new Error().stack);
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
    this.playerSymbol = null;
    this.roomCode = null;
    this.pendingCreateRoom = null;
    this.pendingCreateRoomReject = null;
    this.pendingJoinRoom = null;
    this.pendingJoinRoomReject = null;
  }

  /**
   * Send a message through the WebSocket connection
   * Used by MultiplayerChessService for chess-specific messages
   */
  send(message: any): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(message));
    } else {
      console.error('❌ Cannot send message - WebSocket not connected');
      throw new Error('WebSocket is not connected');
    }
  }

  /**
   * Set a custom message handler for chess or other games
   */
  setCustomMessageHandler(handler: ((message: any) => void) | null): void {
    this.customMessageHandler = handler;
  }

  private sendMessage(message: WebSocketMessage): void {
    if (!this.ws) {
      console.error('❌ Cannot send message - WebSocket is null');
      throw new Error('WebSocket is not initialized');
    }
    
    if (this.ws.readyState !== WebSocket.OPEN) {
      console.error('❌ Cannot send message - WebSocket state:', this.ws.readyState);
      console.error('❌ WebSocket states: CONNECTING=0, OPEN=1, CLOSING=2, CLOSED=3');
      throw new Error(`WebSocket is not open (state: ${this.ws.readyState})`);
    }
    
    try {
      const messageStr = JSON.stringify(message);
      console.log('📤 Sending message:', messageStr);
      this.ws.send(messageStr);
      console.log('✅ Message sent successfully');
    } catch (error) {
      console.error('❌ Failed to send WebSocket message:', error);
      throw error;
    }
  }

  private handleMessage(message: WebSocketMessage): void {
    const msg = message as any;
    const action = message.action || msg.type || '';
    console.log('📥 Received message:', action);

    // Route to custom handler for non-tictactoe game messages
    // Custom handlers are used by chess, checkers, and other games that extend GenericMultiplayerService
    if (this.customMessageHandler) {
      const actionStr = String(action);
      const hasGamePrefix = actionStr.includes(':');
      const gamePrefix = hasGamePrefix ? actionStr.split(':')[0] : '';
      
      // Route to custom handler if:
      // 1. Message has a 'type' field (legacy chess format), OR
      // 2. Action has a game prefix that isn't 'tictactoe' (other games use their own handlers)
      if (msg.type || (hasGamePrefix && gamePrefix !== 'tictactoe')) {
        // Normalize: ensure the message has a 'type' field for custom handlers that expect it
        if (!msg.type && action) {
          msg.type = action;
        }
        console.log('📥 Routing to custom handler:', action);
        this.customMessageHandler(msg);
        return;
      }
    }

    // Parse gameType:action format from Lambda
    const parts = action.split(':');
    const gameAction = parts.length > 1 ? parts[1] : action;

    // Handle messages based on the action part
    switch (gameAction) {
      case 'roomCreated':
      case 'room_created':
        console.log('✅ Room created successfully');
        if (this.pendingCreateRoom) {
          const roomCode = msg.roomCode || message.gameState?.roomCode || this.roomCode;
          this.pendingCreateRoom(roomCode);
          this.pendingCreateRoom = null;
          this.pendingCreateRoomReject = null;
        }
        if (message.gameState && this.onGameStateUpdate) {
          this.onGameStateUpdate(message.gameState);
        }
        break;

      case 'roomJoined':
      case 'room_joined':
        console.log('✅ Room joined successfully');
        if (this.pendingJoinRoom) {
          this.pendingJoinRoom();
          this.pendingJoinRoom = null;
          this.pendingJoinRoomReject = null;
        }
        if (message.gameState && this.onGameStateUpdate) {
          this.onGameStateUpdate(message.gameState);
        }
        break;

      case 'opponentJoined':
      case 'player_joined':
      case 'moveUpdate':
      case 'move_made':
      case 'game_ended':
      case 'game_reset':
        console.log('🎮 Game state updated:', gameAction);
        if (message.gameState && this.onGameStateUpdate) {
          this.onGameStateUpdate(message.gameState);
        } else if (msg.gameState && this.onGameStateUpdate) {
          this.onGameStateUpdate(msg.gameState);
        }
        break;

      case 'opponentDisconnected':
      case 'opponentLeft':
      case 'opponentResigned':
        console.log('👋 Opponent event:', gameAction);
        if (this.onError) {
          this.onError(`Opponent ${gameAction.replace('opponent', '').toLowerCase()}`);
        }
        break;

      case 'error':
        console.error('❌ Server error:', msg.error || message.error);
        
        if (this.pendingCreateRoomReject && (msg.error || message.error)) {
          this.pendingCreateRoomReject(new Error(msg.error || message.error));
          this.pendingCreateRoom = null;
          this.pendingCreateRoomReject = null;
        }
        if (this.pendingJoinRoomReject && (msg.error || message.error)) {
          this.pendingJoinRoomReject(new Error(msg.error || message.error));
          this.pendingJoinRoom = null;
          this.pendingJoinRoomReject = null;
        }
        
        if (this.onError && (msg.error || message.error)) {
          this.onError(msg.error || message.error);
        }
        break;

      default:
        console.warn('❓ Unknown message action:', action, '(parsed as:', gameAction, ')');
        // Try custom handler as fallback
        if (this.customMessageHandler) {
          this.customMessageHandler(message);
        }
    }
  }

  private generateRoomCode(): string {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let result = '';
    // Use crypto.getRandomValues if available (browser), fallback to Math.random (test env)
    if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
      const array = new Uint8Array(6);
      crypto.getRandomValues(array);
      for (let i = 0; i < 6; i++) {
        result += chars.charAt(array[i] % chars.length);
      }
    } else {
      for (let i = 0; i < 6; i++) {
        result += chars.charAt(Math.floor(Math.random() * chars.length));
      }
    }
    return result;
  }
}